import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Bericht, BerichtStatusTyp } from '../types';
import berichtService from '../services/berichtService';
import berichtStatusService from '../services/berichtStatusService';
import { Icon, PageHeader, LoadingScreen, Spinner, EmptyState, FilterStand, FilterZuruecksetzen, AnlegenButton, filterAktiv, fehlerText } from '../components/ui';
import { useStueckweise } from '../utils/stueckweise';
import { ankunft, markiereNeu } from '../utils/ankunft';

const PRESET_COLORS = [
  '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6',
  '#ef4444', '#f97316', '#06b6d4', '#6b7280',
];

// '' = neueste zuerst (wie geladen), 'status' = nach Status gruppiert, 'status-<id>' = dieser Status oben
const SORTIERUNG_KEY = 'hp.berichte.sortierung';

const Berichte: React.FC = () => {
  const [berichte, setBerichte] = useState<Bericht[]>([]);
  const [statusTypen, setStatusTypen] = useState<BerichtStatusTyp[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [downloadingPdfId, setDownloadingPdfId] = useState<number | null>(null);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [openStatusMenuId, setOpenStatusMenuId] = useState<number | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [editingStatus, setEditingStatus] = useState<BerichtStatusTyp | null>(null);
  const [newStatusName, setNewStatusName] = useState('');
  const [newStatusFarbe, setNewStatusFarbe] = useState('#6b7280');
  const [sortierung, setSortierung] = useState<string>(() => {
    try { return localStorage.getItem(SORTIERUNG_KEY) || ''; } catch { return ''; }
  });
  const menuRef = useRef<HTMLDivElement>(null);
  const statusMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => { loadBerichte(); loadStatusTypen(); }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
      if (statusMenuRef.current && !statusMenuRef.current.contains(e.target as Node)) {
        setOpenStatusMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadBerichte = async () => {
    try {
      setBerichte(await berichtService.getAll());
    } catch { toast.error('Berichte konnten nicht geladen werden'); }
    finally { setLoading(false); }
  };

  const loadStatusTypen = async () => {
    try {
      setStatusTypen(await berichtStatusService.getAll());
    } catch { /* silently ignore */ }
  };

  const handleDelete = async (bericht: Bericht) => {
    setOpenMenuId(null);
    const name = [bericht.kundeVorname, bericht.kundeNachnameFirma].filter(Boolean).join(' ');
    if (window.confirm(`Bericht „${name}“ vom ${formatDate(bericht.datum)} löschen?`)) {
      try {
        await berichtService.delete(bericht.id);
        toast.success('Bericht gelöscht');
        await loadBerichte();
      } catch (err: any) {
        toast.error(fehlerText(err, 'Bericht konnte nicht gelöscht werden'));
      }
    }
  };

  const handleToggleExpand = (bericht: Bericht, isExpanded: boolean) => {
    setExpandedId(isExpanded ? null : bericht.id);
  };

  const handleDownloadPdf = async (bericht: Bericht) => {
    setOpenMenuId(null);
    setDownloadingPdfId(bericht.id);
    try {
      await berichtService.downloadPdf(bericht.id);
      toast.success('PDF heruntergeladen');
    } catch (err: any) {
      toast.error(fehlerText(err, 'PDF konnte nicht erzeugt werden'));
    } finally {
      setDownloadingPdfId(null);
    }
  };

  const handleStatusChange = async (bericht: Bericht, statusId: number | null) => {
    setOpenStatusMenuId(null);
    try {
      await berichtService.updateStatus(bericht.id, statusId);
      const markGelesen = !bericht.gelesen
        ? berichtService.markAsGelesen(bericht.id).catch(() => {})
        : Promise.resolve();
      await markGelesen;
      const status = statusTypen.find(s => s.id === statusId);
      setBerichte(prev => prev.map(b =>
        b.id === bericht.id
          ? { ...b, statusId: statusId ?? undefined, statusName: status?.name, statusFarbe: status?.farbe, gelesen: true }
          : b
      ));
      markiereNeu(`bericht-${bericht.id}`);
    } catch (err: any) {
      toast.error(fehlerText(err, 'Status konnte nicht geändert werden'));
    }
  };

  // Status management
  const handleSaveStatus = async () => {
    if (!newStatusName.trim()) return;
    try {
      if (editingStatus) {
        const updated = await berichtStatusService.update(editingStatus.id, {
          name: newStatusName.trim(),
          farbe: newStatusFarbe,
          reihenfolge: editingStatus.reihenfolge,
        });
        setStatusTypen(prev => prev.map(s => s.id === updated.id ? updated : s));
        setBerichte(prev => prev.map(b =>
          b.statusId === updated.id ? { ...b, statusName: updated.name, statusFarbe: updated.farbe } : b
        ));
        toast.success('Status gespeichert');
      } else {
        const created = await berichtStatusService.create({
          name: newStatusName.trim(),
          farbe: newStatusFarbe,
          reihenfolge: statusTypen.length,
        });
        setStatusTypen(prev => [...prev, created]);
        toast.success('Status angelegt');
      }
      setEditingStatus(null);
      setNewStatusName('');
      setNewStatusFarbe('#6b7280');
    } catch (err: any) {
      toast.error(fehlerText(err, 'Status konnte nicht gespeichert werden'));
    }
  };

  const handleDeleteStatus = async (status: BerichtStatusTyp) => {
    if (!window.confirm(`Status „${status.name}“ löschen?`)) return;
    try {
      await berichtStatusService.delete(status.id);
      setStatusTypen(prev => prev.filter(s => s.id !== status.id));
      toast.success('Status gelöscht');
    } catch (err: any) {
      toast.error(fehlerText(err, 'Status konnte nicht gelöscht werden'));
    }
  };

  const startEditStatus = (status: BerichtStatusTyp) => {
    setEditingStatus(status);
    setNewStatusName(status.name);
    setNewStatusFarbe(status.farbe);
  };

  const cancelEditStatus = () => {
    setEditingStatus(null);
    setNewStatusName('');
    setNewStatusFarbe('#6b7280');
  };

  const handleSortierungChange = (wert: string) => {
    setSortierung(wert);
    try { localStorage.setItem(SORTIERUNG_KEY, wert); } catch { /* egal */ }
  };

  const formatDate = (d: string) => {
    if (!d) return '';
    const parts = d.split('-');
    if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
    return d;
  };

  const filteredBerichte = berichte.filter(b => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    const kundeName = [b.kundeVorname, b.kundeNachnameFirma].filter(Boolean).join(' ').toLowerCase();
    return kundeName.includes(term);
  });
  const filterIstAktiv = searchTerm.trim() !== '';

  // Gelöschter Status in der gemerkten Sortierung → wieder neueste zuerst
  const obenStatusId = sortierung.startsWith('status-') ? Number(sortierung.slice('status-'.length)) : null;
  const sortierungGueltig = obenStatusId === null || statusTypen.some(s => s.id === obenStatusId);
  const statusRang = (b: Bericht): number => {
    if (b.statusId == null) return Number.MAX_SAFE_INTEGER;
    if (obenStatusId !== null) return b.statusId === obenStatusId ? 0 : 1;
    const i = statusTypen.findIndex(s => s.id === b.statusId);
    return i === -1 ? Number.MAX_SAFE_INTEGER - 1 : i;
  };
  // Array.sort ist stabil: innerhalb eines Status bleibt neueste zuerst erhalten
  const sortierteBerichte = sortierung === '' || !sortierungGueltig
    ? filteredBerichte
    : [...filteredBerichte].sort((x, y) => statusRang(x) - statusRang(y));
  const { sichtbar: sichtbareBerichte, Ende } = useStueckweise(sortierteBerichte);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="p-4 md:p-6">
      <PageHeader
        title="Arbeitsberichte"
        actions={<>
          <button
            onClick={() => setShowStatusModal(true)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Status verwalten"
            aria-label="Status verwalten"
          >
            <Icon.Regler className="w-5 h-5" />
          </button>
          <AnlegenButton onClick={() => navigate('/berichte/neu')}>Bericht anlegen</AnlegenButton>
        </>}
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[12rem]">
          <Icon.Search className={`pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 ${filterIstAktiv ? 'text-brand-600' : 'text-gray-400'}`} />
          <input
            type="text"
            placeholder="Nach Kundenname suchen"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full p-2 pl-8 border border-gray-300 rounded-lg text-sm ${filterIstAktiv ? filterAktiv : ''}`}
          />
        </div>
        <select
          value={sortierungGueltig ? sortierung : ''}
          onChange={(e) => handleSortierungChange(e.target.value)}
          className="p-2 border border-gray-300 rounded-lg text-sm bg-white"
          aria-label="Sortierung"
          title="Sortierung"
        >
          <option value="">Neueste zuerst</option>
          {statusTypen.length > 0 && <option value="status">Nach Status</option>}
          {statusTypen.length > 0 && (
            <optgroup label="Status oben">
              {statusTypen.map(s => (
                <option key={s.id} value={`status-${s.id}`}>{s.name} oben</option>
              ))}
            </optgroup>
          )}
        </select>
        <FilterStand treffer={filteredBerichte.length} gesamt={berichte.length} einheit="Berichte"
          aktiv={filterIstAktiv} onZuruecksetzen={() => setSearchTerm('')} />
      </div>

      {berichte.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100">
          <EmptyState icon={<Icon.Bericht className="h-6 w-6" />} title="Noch keine Berichte"
            hint="Nach einem Einsatz beim Kunden hier den Arbeitsbericht erfassen."
            action={<AnlegenButton onClick={() => navigate('/berichte/neu')}>Bericht anlegen</AnlegenButton>} />
        </div>
      )}

      {berichte.length > 0 && filteredBerichte.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100">
          <EmptyState icon={<Icon.Search className="h-6 w-6" />} title={`Keine Berichte für „${searchTerm}“`}
            action={<FilterZuruecksetzen onClick={() => setSearchTerm('')} />} />
        </div>
      )}

      <div className="space-y-2">
        {sichtbareBerichte.map((bericht) => {
          const kundeName = [bericht.kundeVorname, bericht.kundeNachnameFirma].filter(Boolean).join(' ');
          const isExpanded = expandedId === bericht.id;
          const arbeitszeitNetto = bericht.arbeitszeitStart && bericht.arbeitszeitEnde
            ? calcMinutes(bericht.arbeitszeitStart, bericht.arbeitszeitEnde) - bericht.pauseMinuten
            : 0;
          const isDownloading = downloadingPdfId === bericht.id;
          const isMenuOpen = openMenuId === bericht.id;
          const isStatusMenuOpen = openStatusMenuId === bericht.id;
          const a = ankunft(`bericht-${bericht.id}`);

          return (
            <div key={bericht.id} ref={a.ref} className={`bg-white rounded-lg shadow-sm border border-gray-100 ${a.className}`}>
              {/* Header row - always visible */}
              <div className="flex items-center p-3 md:p-4">
                {/* Clickable content area */}
                <div
                  className="flex-1 min-w-0 cursor-pointer"
                  onClick={() => handleToggleExpand(bericht, isExpanded)}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="text-sm font-semibold text-gray-900 hover:text-brand-700 truncate cursor-pointer"
                      onClick={(e) => { e.stopPropagation(); navigate(`/kunden/${bericht.kundeId}`); }}
                      title={`${kundeName} anzeigen`}
                    >
                      {kundeName}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-xs text-gray-500">
                    <span className="whitespace-nowrap">{formatDate(bericht.datum)}</span>
                    <span className="whitespace-nowrap">{bericht.fahrzeitMinuten} Min. Fahrt</span>
                    <span className="whitespace-nowrap">{arbeitszeitNetto} Min. Arbeit</span>
                    <span className="hidden sm:inline whitespace-nowrap">{bericht.gefahreneKm} km</span>
                  </div>
                </div>

                {/* Status badge with dropdown */}
                <div
                  className="relative flex-shrink-0 mx-2"
                  ref={isStatusMenuOpen ? statusMenuRef : undefined}
                >
                  <button
                    key={a.className ? `status-${bericht.statusId ?? 'ohne'}` : 'status'}
                    onClick={(e) => { e.stopPropagation(); setOpenStatusMenuId(isStatusMenuOpen ? null : bericht.id); setOpenMenuId(null); }}
                    className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-white transition-opacity hover:opacity-80 ${a.className ? 'ui-wechsel' : ''}`}
                    style={{ backgroundColor: bericht.statusFarbe || '#6b7280' }}
                    title="Status ändern"
                  >
                    {bericht.statusName || 'kein Status'}
                    <Icon.ChevronDown className="w-3 h-3" />
                  </button>

                  {isStatusMenuOpen && (
                    <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-lg shadow-lg border border-gray-200 z-40 py-1">
                      <div className="px-3 py-1.5 text-xs font-semibold text-gray-500">Status setzen</div>
                      {statusTypen.map(status => (
                        <button
                          key={status.id}
                          onClick={(e) => { e.stopPropagation(); handleStatusChange(bericht, status.id); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: status.farbe }}></span>
                          {status.name}
                          {bericht.statusId === status.id && (
                            <Icon.Check className="w-3.5 h-3.5 ml-auto text-brand-600" />
                          )}
                        </button>
                      ))}
                      {bericht.statusId && (
                        <>
                          <div className="border-t border-gray-100 my-1"></div>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleStatusChange(bericht, null); }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-400 hover:bg-gray-50 transition-colors"
                          >
                            Status entfernen
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* PDF quick button */}
                <button
                  onClick={() => handleDownloadPdf(bericht)}
                  disabled={isDownloading}
                  className="flex-shrink-0 p-2 mr-1 text-brand-600 hover:bg-brand-50 rounded-lg transition-colors disabled:opacity-50"
                  title="Als PDF herunterladen"
                >
                  {isDownloading ? (
                    <Spinner className="h-5 w-5" />
                  ) : (
                    <Icon.Download className="h-5 w-5" />
                  )}
                </button>

                {/* Three-dot menu */}
                <div className="relative flex-shrink-0" ref={isMenuOpen ? menuRef : undefined}>
                  <button
                    onClick={() => setOpenMenuId(isMenuOpen ? null : bericht.id)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label="Weitere Aktionen"
                  >
                    <Icon.Mehr className="w-5 h-5" />
                  </button>

                  {isMenuOpen && (
                    <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-30 py-1">
                      <button
                        onClick={() => { setOpenMenuId(null); handleToggleExpand(bericht, isExpanded); }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Icon.Eye className="w-4 h-4 text-gray-400" />
                        {isExpanded ? 'Zuklappen' : 'Details anzeigen'}
                      </button>
                      <button
                        onClick={() => { setOpenMenuId(null); navigate(`/berichte/${bericht.id}/bearbeiten`); }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Icon.Edit className="w-4 h-4 text-gray-400" />
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => handleDownloadPdf(bericht)}
                        disabled={isDownloading}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                      >
                        <Icon.Download className="w-4 h-4 text-gray-400" />
                        PDF herunterladen
                      </button>
                      <div className="border-t border-gray-100 my-1"></div>
                      <button
                        onClick={() => handleDelete(bericht)}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Icon.Trash className="w-4 h-4" />
                        Löschen
                      </button>
                    </div>
                  )}
                </div>

                {/* Expand chevron */}
                <button
                  onClick={() => handleToggleExpand(bericht, isExpanded)}
                  className="flex-shrink-0 p-1 text-gray-400"
                  aria-label={isExpanded ? 'Zuklappen' : 'Details anzeigen'}
                >
                  <Icon.ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div className="ui-aufklappen border-t border-gray-100 p-3 md:p-4 bg-gray-50 rounded-b-lg">
                  {/* Ausgeführte Arbeiten */}
                  <div className="mb-3">
                    <h3 className="text-xs font-semibold text-gray-500 mb-1">Ausgeführte Arbeiten</h3>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{bericht.ausgefuehrteArbeiten}</p>
                  </div>

                  {/* Zeiten */}
                  <div className="mb-3">
                    <h3 className="text-xs font-semibold text-gray-500 mb-1">Zeiten</h3>
                    <div className="text-sm text-gray-700 space-y-0.5">
                      <div>Fahrzeit: {bericht.abfahrtZeit} - {bericht.ankunftZeit} ({bericht.fahrzeitMinuten} Min.)</div>
                      <div>Arbeitszeit: {bericht.arbeitszeitStart} - {bericht.arbeitszeitEnde} ({arbeitszeitNetto} Min. netto)</div>
                      {bericht.pauseMinuten > 0 && <div>Pause: {bericht.pauseMinuten} Min.</div>}
                      <div>Gefahrene km: {bericht.gefahreneKm}</div>
                    </div>
                  </div>

                  {/* Hinweise */}
                  {bericht.hinweise && (
                    <div className="mb-3">
                      <h3 className="text-xs font-semibold text-gray-500 mb-1">Hinweise</h3>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{bericht.hinweise}</p>
                    </div>
                  )}

                  {/* Material */}
                  {bericht.materialien && bericht.materialien.length > 0 && (
                    <div className="mb-3">
                      <h3 className="text-xs font-semibold text-gray-500 mb-1">Material</h3>
                      <div className="text-sm text-gray-700">
                        {bericht.materialien.map((m, i) => (
                          <div key={i}>{m.menge} {m.einheit} {m.artikelbezeichnung}</div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Mitarbeiter */}
                  {bericht.mitarbeiter && bericht.mitarbeiter.length > 0 && (
                    <div className="mb-3">
                      <h3 className="text-xs font-semibold text-gray-500 mb-1">Mitarbeiter</h3>
                      <div className="text-sm text-gray-700">
                        {bericht.mitarbeiter.map((m, i) => (
                          <div key={i}>{m.name}</div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Unterschrift + Erstellt von */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    {bericht.unterschriftBild && (
                      <div>
                        <h3 className="text-xs font-semibold text-gray-500 mb-1">Unterschrift</h3>
                        <img src={bericht.unterschriftBild} alt="Unterschrift"
                          className="border border-gray-200 rounded bg-white max-h-20" />
                        {bericht.unterschriftName && (
                          <p className="text-xs text-gray-500 mt-1">{bericht.unterschriftName}</p>
                        )}
                      </div>
                    )}
                    {bericht.erstelltVon && (
                      <div>
                        <h3 className="text-xs font-semibold text-gray-500 mb-1">Erstellt von</h3>
                        <p className="text-sm text-gray-700">{bericht.erstelltVon}</p>
                      </div>
                    )}
                  </div>

                  {/* Action buttons in expanded view */}
                  <div className="mt-4 pt-3 border-t border-gray-200 flex flex-wrap gap-2">
                    <button
                      onClick={() => navigate(`/berichte/${bericht.id}/bearbeiten`)}
                      className="bg-brand-600 text-white px-4 py-2 rounded-lg hover:bg-brand-700 transition-colors flex items-center gap-2 text-sm"
                    >
                      <Icon.Edit className="h-4 w-4" />
                      Bearbeiten
                    </button>
                    <button
                      onClick={() => handleDownloadPdf(bericht)}
                      disabled={isDownloading}
                      className="bg-white border border-brand-200 text-brand-700 px-4 py-2 rounded-lg hover:bg-brand-50 transition-colors disabled:opacity-50 flex items-center gap-2 text-sm"
                    >
                      {isDownloading ? (
                        <Spinner className="h-4 w-4" />
                      ) : (
                        <Icon.Download className="h-4 w-4" />
                      )}
                      PDF
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {Ende}
      </div>

      {/* Status Management Modal */}
      {showStatusModal && (
        <div className="ui-fade fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="ui-pop bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Status verwalten</h2>
              <button onClick={() => { setShowStatusModal(false); cancelEditStatus(); }} className="text-gray-400 hover:text-gray-600" aria-label="Schließen">
                <Icon.Close className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-5">
              {/* Existing statuses */}
              <div className="space-y-2 mb-5">
                {statusTypen.map(status => (
                  <div key={status.id} className="flex items-center gap-2 p-2 rounded-lg border border-gray-100 hover:border-gray-200">
                    <span className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: status.farbe }}></span>
                    <span className="flex-1 text-sm text-gray-800">{status.name}</span>
                    <button onClick={() => startEditStatus(status)} className="p-1 text-gray-400 hover:text-gray-600 rounded">
                      <Icon.Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDeleteStatus(status)} className="p-1 text-gray-400 hover:text-red-500 rounded">
                      <Icon.Trash className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {statusTypen.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-2">Noch keine Status vorhanden.</p>
                )}
              </div>

              {/* Add / Edit form */}
              <div className="border-t border-gray-100 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  {editingStatus ? `Status „${editingStatus.name}“ bearbeiten` : 'Neuer Status'}
                </h3>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={newStatusName}
                    onChange={e => setNewStatusName(e.target.value)}
                    placeholder="Statusname"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                    onKeyDown={e => e.key === 'Enter' && handleSaveStatus()}
                  />
                  <div>
                    <div className="text-xs text-gray-500 mb-2">Farbe</div>
                    <div className="flex flex-wrap gap-2 items-center">
                      {PRESET_COLORS.map(color => (
                        <button
                          key={color}
                          onClick={() => setNewStatusFarbe(color)}
                          className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${newStatusFarbe === color ? 'border-gray-800 scale-110' : 'border-transparent'}`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                      <input
                        type="color"
                        value={newStatusFarbe}
                        onChange={e => setNewStatusFarbe(e.target.value)}
                        className="w-7 h-7 rounded-full border border-gray-300 cursor-pointer p-0"
                        title="Eigene Farbe"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveStatus}
                      disabled={!newStatusName.trim()}
                      className="flex-1 bg-brand-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50 transition-colors"
                    >
                      {editingStatus ? 'Speichern' : 'Status anlegen'}
                    </button>
                    {editingStatus && (
                      <button
                        onClick={cancelEditStatus}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                      >
                        Abbrechen
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function calcMinutes(start: string, end: string): number {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const diff = (eh * 60 + em) - (sh * 60 + sm);
  return diff > 0 ? diff : 0;
}

export default Berichte;
